import {useApi, useClient} from '@/haligator-factory';
import {useDefaultOnError} from '@/hooks/useDefaultOnError';
import {useSimpleTextToast} from '@/hooks/useSimpleTextToast';
import {Box, Modal, ModalContent, ModalOverlay} from '@chakra-ui/react';
import NiceModal, {useModal} from '@ebay/nice-modal-react';
import {useCommand} from '@synako/haligator';
import {Navigable, State} from '@synako/halitrek';
import {
  ConversationDesc,
  ConversationInitieeEventDesc,
  ParticipationDesc,
} from 'api-types/ffconnect-hal';
import {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {ModaleInitierNouvelleConversation} from '@/backoffice/domains/CanalCollaboratif/ModaleInitierNouvelleConversation';
import {ConversationClientDetailView} from './ConversationClientDetailView';
import {ConversationClientDetailConversationView} from './ConversationClientDetailConversationView';
import {ConversationClientNouvelleView} from './ConversationClientNouvelleView';
import {
  ConversationsClientListeView,
  type OngletConversation,
} from './ConversationsClientListeView';

// Type de vue affichée dans le drawer
type VueConversation = 'liste' | 'nouvelle' | 'detail' | 'detail-conversation';

/**
 * Modale des conversations pour l'interface client.
 * Gère la navigation entre la liste des conversations, la création et le détail.
 *
 * Utilisée aussi en mode commercial (prise de commande) et dans la fiche client BO :
 * dans ce dernier cas, `clientId` cible explicitement le client de la fiche pour l'initiation
 * d'une nouvelle conversation (hors fiche, on retombe sur le client du contexte via useClient).
 */
export const ConversationsClientModale = NiceModal.create(
  ({clientId}: {clientId?: string}) => {
    const modal = useModal();
    const api = useApi();
    const textToast = useSimpleTextToast();
    const onError = useDefaultOnError(textToast);

    // Vue actuellement affichée
    const [vueActive, setVueActive] = useState<VueConversation>('liste');

    // Participation sélectionnée pour la vue détail (mes conversations)
    const [participationSelectionnee, setParticipationSelectionnee] =
      useState<Navigable<ParticipationDesc> | null>(null);

    // Conversation périmètre sélectionnée pour la vue détail (onglet "Toutes")
    const [
      conversationPerimetreSelectionnee,
      setConversationPerimetreSelectionnee,
    ] = useState<Navigable<ConversationDesc> | null>(null);

    // Onglet actif de la liste, remonté ici pour survivre aux allers-retours liste ↔ détail
    // (la liste est démontée pendant l'affichage d'un détail).
    const [ongletActif, setOngletActif] =
      useState<OngletConversation>('en_cours');

    // Client cible pour l'initiation d'une conversation :
    // - fiche client BO : le client explicite passé en prop
    // - sinon : le client du contexte (client connecté, ou client impersonné en mode commercial)
    const clientContextuel = useClient();
    const clientNav = clientId
      ? api.follow('get-client', {client_id: clientId})
      : clientContextuel;

    // client_id depuis l'URL en mode commercial (/c/:client_id) ; sinon la prop (fiche client BO).
    // Présent uniquement dans ces contextes commerciaux (jamais en interface client).
    const {client_id: clientIdFromUrl} = useParams<{client_id?: string}>();
    const resolvedClientId = clientId ?? clientIdFromUrl;

    // Ouvre le détail de "ma participation" à partir de l'event d'initiation : l'initiateur est un
    // contact_client côté client, un utilisateur_ff côté commercial. On le retrouve via le type
    // d'expéditeur du message. La conversation créée étant désormais dans "Mes conversations", on
    // s'y positionne aussi (utile au retour depuis le détail).
    const ouvrirMaParticipationDepuisEvent = (
      eventState: State<ConversationInitieeEventDesc>,
    ) => {
      const {participants, message} = eventState.data.data;
      const maParticipation = participants.find(
        (p) => p.participant_type === message.expediteur_type,
      );
      setOngletActif('en_cours');
      // Sécurité : sans participation exploitable, on évite un GET get-participation vide (404).
      if (!maParticipation) {
        setParticipationSelectionnee(null);
        setVueActive('liste');
        return;
      }
      setParticipationSelectionnee(
        api.follow('get-participation', {participation_id: maParticipation.id}),
      );
      setVueActive('detail');
    };

    // Commande pour initier une nouvelle conversation (flux contact client)
    const {submit: submitInitierConversation, isSubmitting} = useCommand(
      clientNav,
      'c:initier-conversation',
      {
        onSuccess: ouvrirMaParticipationDepuisEvent,
        onError,
      },
    );

    const handleOuvrirConversation = (
      participationNav: Navigable<ParticipationDesc>,
    ) => {
      setParticipationSelectionnee(participationNav);
      setVueActive('detail');
    };

    const handleOuvrirConversationPerimetre = (
      conversationNav: Navigable<ConversationDesc>,
    ) => {
      setConversationPerimetreSelectionnee(conversationNav);
      setVueActive('detail-conversation');
    };

    const handleEnvoyerNouvelleConversation = (
      titre: string,
      message: string,
      fichiers: File[],
    ) => {
      const formData = new FormData();
      formData.append('titre', titre);
      formData.append('contenu', message);
      for (const fichier of fichiers) {
        formData.append('pieces_jointes', fichier);
      }
      submitInitierConversation(formData);
    };

    // "Commencer une conversation" : avec un client cible (fiche client / mode commercial), le
    // commercial passe par la modale BO pour choisir le contact destinataire ; sans client cible
    // (interface client), le contact client est son propre destinataire → vue de création simple.
    const handleNouvelleConversation = () => {
      if (resolvedClientId) {
        NiceModal.show(ModaleInitierNouvelleConversation, {
          preselectedClientNav: clientNav,
          preselectedClientId: resolvedClientId,
          // Au succès, on ouvre directement le détail de la conversation créée dans la popin.
          onConversationCreated: ouvrirMaParticipationDepuisEvent,
        });
      } else {
        setVueActive('nouvelle');
      }
    };

    const handleRetourVersListe = () => {
      setParticipationSelectionnee(null);
      setConversationPerimetreSelectionnee(null);
      setVueActive('liste');
    };

    // Après "Rejoindre", on bascule sur "Mes conversations" : la conversation rejointe y apparaît
    // (find-participations est invalidé par l'event) au lieu de rester sur "Toutes les conversations".
    const handleConversationRejointe = () => {
      setOngletActif('en_cours');
      handleRetourVersListe();
    };

    // Rediriger vers la liste si on est en vue détail sans sélection
    useEffect(() => {
      if (vueActive === 'detail' && !participationSelectionnee) {
        setVueActive('liste');
      }
      if (
        vueActive === 'detail-conversation' &&
        !conversationPerimetreSelectionnee
      ) {
        setVueActive('liste');
      }
    }, [
      vueActive,
      participationSelectionnee,
      conversationPerimetreSelectionnee,
    ]);

    // Rendu de la vue active
    // Selon vueActive : liste des conversations, création, détail (ma participation) ou détail périmètre
    const renderVueActive = () => {
      switch (vueActive) {
        case 'liste':
          return (
            // Liste des conversations
            <ConversationsClientListeView
              clientId={resolvedClientId}
              onFermer={modal.hide}
              onNouvelleConversation={handleNouvelleConversation}
              onOuvrirConversation={handleOuvrirConversation}
              onOuvrirConversationPerimetre={handleOuvrirConversationPerimetre}
              ongletActif={ongletActif}
              onChangeOnglet={setOngletActif}
            />
          );
        case 'nouvelle':
          return (
            // Création d'une nouvelle conversation
            <ConversationClientNouvelleView
              onRetour={handleRetourVersListe}
              onFermer={modal.hide}
              onEnvoyer={handleEnvoyerNouvelleConversation}
              isSubmitting={isSubmitting}
            />
          );
        case 'detail':
          if (!participationSelectionnee) {
            return null; // Le useEffect redirigera vers la liste
          }
          return (
            // Détail d'une conversation existante avec les messages (ma participation)
            <ConversationClientDetailView
              participationNav={participationSelectionnee}
              onRetour={handleRetourVersListe}
              onFermer={modal.hide}
            />
          );
        case 'detail-conversation':
          if (!conversationPerimetreSelectionnee) {
            return null; // Le useEffect redirigera vers la liste
          }
          return (
            // Détail d'une conversation du périmètre (onglet "Toutes les conversations")
            <ConversationClientDetailConversationView
              conversationNav={conversationPerimetreSelectionnee}
              onRetour={handleRetourVersListe}
              onFermer={modal.hide}
              onConversationRejointe={handleConversationRejointe}
            />
          );
      }
    };

    return (
      <Modal
        isOpen={modal.visible}
        onClose={modal.hide}
        motionPreset='none'
      >
        <ModalOverlay />
        <ModalContent
          bg='white'
          w={{base: '100%', md: '480px'}}
          maxW={{base: '100%', md: '480px'}}
          h={{base: '100vh', md: '650px'}}
          maxH={{base: '100vh', md: '650px'}}
          position={{base: 'fixed', md: 'fixed'}}
          bottom={{base: 0, md: '20px'}}
          right={{base: 0, md: '20px'}}
          top={{base: 0, md: 'auto'}}
          left={{base: 0, md: 'auto'}}
          m={0}
          borderRadius={{base: 0, md: '12px'}}
          boxShadow='xl'
          transformOrigin='bottom right'
          containerProps={{
            justifyContent: 'flex-end',
            alignItems: 'flex-end',
          }}
          initial={{opacity: 0, scale: 0.3}}
          animate={{opacity: 1, scale: 1}}
          exit={{opacity: 0, scale: 0.3}}
          // @ts-expect-error Chakra UI n'expose pas les types framer-motion (comme transition) mais ils fonctionnent à l'exécution
          transition={{duration: 0.4, ease: 'easeOut'}}
        >
          <Box
            h='100%'
            display='flex'
            flexDirection='column'
          >
            {renderVueActive()}
          </Box>
        </ModalContent>
      </Modal>
    );
  },
);
